import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './ManageCardsModal.module.scss';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { SvgIcon, SvgIconSrc } from '@/components/SvgIcon';
import { useSelector } from 'react-redux';
import { addNotification, notificationStates } from '@/components/Notification';
import httpClient from '@/config/httpClient';
import { Button, buttonVariants } from '@/components/Button';
const isRecurringTokenConfirmed = (tokenId, activeCardTokenId) =>
  tokenId === activeCardTokenId;

const cardTokensFromResponse = (payload) => {
  if (!payload) return [];
  const raw = payload.items ?? payload;
  const list = Array.isArray(raw) ? raw : [];
  return list.filter((t) => t?.method === 'card' && t?.card);
};

const iconSrcForNetwork = (network) => {
  const n = String(network || '').toLowerCase();
  if (n.includes('visa')) return SvgIconSrc.visa;
  if (n.includes('master') || n === 'mc' || n.includes('mastercard')) {
    return SvgIconSrc.mastercard;
  }
  return null;
};

export const ManageCardsModal = () => {
  const subscription = useSelector(
    (state) => state.auth?.profile?.subscription
  );
  const companyId = useSelector(
    (state) => state.auth?.profile?.activeCompany?.companyId
  );
  const { razorpayCustomerId, subscriptionId } = useMemo(() => {
    return {
      razorpayCustomerId: subscription?.razorpayCustomerId,
      subscriptionId: subscription?.subscriptionId,
    };
  }, [subscription]);
  const { activeCardDetails } = useMemo(() => {
    return {
      activeCardDetails: subscription?.lastPayment?.cardDetails,
    };
  }, [subscription]);

  const [tokens, setTokens] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deletingTokenId, setDeletingTokenId] = useState(null);

  const fetchTokens = async (customerId) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await httpClient.post('/fetchRazorpayTokens', {
        customerId,
      });
      const data = response.data;
      if (!data.success) {
        setTokens([]);
        setError(data.message || `Could not load cards (${data.status})`);
        return;
      }
      setTokens(cardTokensFromResponse(data.tokens));
    } catch (e) {
      setTokens([]);
      setError(e.message || 'Could not load saved cards');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteToken = useCallback(async (customerId, tokenId) => {
    setDeletingTokenId(tokenId);
    try {
      const response = await httpClient.post('/deleteRazorpayToken', {
        customerId,
        tokenId,
      });
      const data = response.data;
      if (!data.success) {
        addNotification({
          level: notificationStates.error,
          message:
            data.message ||
            `Could not remove card (${response.status}). Try again.`,
          autoDismiss: 5,
        });
        return;
      }
      await fetchTokens(customerId);
      addNotification({
        level: notificationStates.success,
        message: 'Card removed from saved payment methods.',
        autoDismiss: 4,
      });
    } catch (e) {
      addNotification({
        level: notificationStates.error,
        message: e.message || 'Could not remove card. Network error.',
        autoDismiss: 5,
      });
    } finally {
      setDeletingTokenId(null);
    }
  }, []);

  useEffect(() => {
    if (!razorpayCustomerId) {
      setTokens([]);
      setError(null);
      setIsLoading(false);
      return;
    }
    fetchTokens(razorpayCustomerId);
  }, [razorpayCustomerId]);

  const emptyMessage = useMemo(() => {
    if (!razorpayCustomerId) {
      return 'No billing profile is linked yet. Complete a subscription checkout to save a card.';
    }
    if (!isLoading && !error && tokens.length === 0) {
      return 'No saved cards on file.';
    }
    return null;
  }, [razorpayCustomerId, isLoading, error, tokens.length]);
  const handleSubscriptionCardChange = async (paymentId) => {
    try {
      const response = await httpClient.post('/updateSubscriptionCard', {
        companyId: companyId,
        paymentId: paymentId,
      });
      const data = response.data;
      if (!data.success) {
        throw new Error(data.message || 'Failed to update subscription card');
      }
      addNotification({
        level: notificationStates.success,
        message: 'Subscription card updated successfully',
        autoDismiss: 4,
      });
    } catch (error) {
      addNotification({
        level: notificationStates.error,
        message: error.message || 'Failed to update subscription card',
        autoDismiss: 4,
      });
    }
  };
  const handleAddCard = () => {
    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      subscription_id: subscriptionId,
      subscription_card_change: 1,
      handler: (response) => {
        if (response.razorpay_payment_id) {
          handleSubscriptionCardChange(response.razorpay_payment_id);
        }
      },
    };
    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  return (
    <div className={styles.manageCardsModal}>
      {isLoading && (
        <div className={styles.loadingRow}>
          <div className={styles.spinner} aria-hidden />
          <Text
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Medium}
            color="gray-600"
          >
            Loading saved cards…
          </Text>
        </div>
      )}

      {!isLoading && error && (
        <div className={styles.message}>
          <Text
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Medium}
            color="gray-700"
          >
            {error}
          </Text>
        </div>
      )}

      {!isLoading && emptyMessage && (
        <div className={styles.message}>
          <Text
            variant={textVariantsEnum.Text_sm}
            fontWeight={fontWeightEnum.Medium}
            color="gray-600"
          >
            {emptyMessage}
          </Text>
        </div>
      )}

      {!isLoading && !error && tokens.length > 0 && (
        <div className={styles.cardsList}>
          <Text
            variant={textVariantsEnum.Text_md}
            fontWeight={fontWeightEnum.Medium}
          >
            Saved Cards
          </Text>
          {tokens.map((token) => {
            const { network, last4, expiry_month, expiry_year } = token.card;
            const iconSrc = iconSrcForNetwork(network);
            const cardLabel = `${network} ·••• ${last4}`;
            const isDeleting = deletingTokenId === token.id;
            const deleteBlocked = isRecurringTokenConfirmed(
              token.id,
              activeCardDetails?.tokenId
            );
            return (
              <div
                className={styles.card}
                key={token.id}
                data-deleting={isDeleting ? 'true' : undefined}
              >
                {iconSrc ? (
                  <SvgIcon src={iconSrc} size={44} />
                ) : (
                  <div className={styles.cardIconPlaceholder} aria-hidden />
                )}
                <div className={styles.cardInfo}>
                  <Text
                    variant={textVariantsEnum.Text_sm}
                    fontWeight={fontWeightEnum.Medium}
                  >
                    {network} ending in {last4}
                  </Text>
                  <Text
                    variant={textVariantsEnum.Text_xs}
                    fontWeight={fontWeightEnum.Medium}
                    color={'gray-600'}
                  >
                    Exp. date: {expiry_month}/{expiry_year}
                  </Text>
                  {deleteBlocked && (
                    <Text
                      variant={textVariantsEnum.Text_xs}
                      fontWeight={fontWeightEnum.Medium}
                      color={'gray-600'}
                    >
                      Used for your active subscription. This card cannot be
                      removed until the subscription is cancelled or updated.
                    </Text>
                  )}
                </div>
                <SvgIcon
                  src={SvgIconSrc.delete}
                  className={styles.deleteIcon}
                  size={16}
                  disabled={!!deletingTokenId || deleteBlocked}
                  title={
                    deleteBlocked
                      ? 'This card is linked to an active subscription.'
                      : undefined
                  }
                  aria-label={
                    deleteBlocked
                      ? `${cardLabel} cannot be removed (active subscription)`
                      : `Remove ${cardLabel}`
                  }
                  onClick={() => {
                    deleteToken(razorpayCustomerId, token.id);
                  }}
                />
              </div>
            );
          })}
          <Button
            text="Add Card"
            className={styles.addCardButton}
            variant={buttonVariants.primary}
            onClick={handleAddCard}
          />
        </div>
      )}
    </div>
  );
};
